import { ROUTES } from '../lib/routes';
import { navigate } from '../router';
import { el } from '../utils/dom';
import { INSTALL_CASE_ITEMS, type InstallCaseItem, installCaseFacilityBadgeClass, installCaseInstallTypeBadgeClass } from '../data/installCaseBoard';
import { showModal } from '../components/toast';
import { ev123EvcUrl } from '../lib/config';

type FacilityKey = 'all' | 'apt' | 'shop' | 'office' | 'public' | 'etc';

const FACILITY_LABEL: Record<Exclude<FacilityKey, 'all'>, string> = {
  apt: '공동주택',
  shop: '상가',
  office: '업무시설',
  public: '공공기관',
  etc: '기타',
};

const FILTER_DEFS: [FacilityKey, string][] = [
  ['all', '전체'],
  ['apt', '공동주택'],
  ['shop', '상가'],
  ['office', '업무시설'],
  ['public', '공공기관'],
  ['etc', '기타'],
];

type PublicCase = {
  id: number | string;
  title: string;
  facility_type: string;
  region: string;
  charger_count: string;
  install_type: string;
  summary: string;
  description: string;
  main_image_url: string;
  image_1_url?: string;
  image_2_url?: string;
  image_3_url?: string;
};

function staticToPublic(item: InstallCaseItem): PublicCase {
  return {
    id: item.id,
    title: item.title,
    facility_type: item.facility_type,
    region: item.region,
    charger_count: item.charger_count,
    install_type: item.install_type,
    summary: item.summary,
    description: item.description,
    main_image_url: item.main_image_url || '',
    image_1_url: item.image_1_url,
    image_2_url: item.image_2_url,
    image_3_url: item.image_3_url,
  };
}

function parsePublicCase(raw: unknown): PublicCase | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = o.id;
  const title = o.title;
  const facility_type = o.facility_type;
  const region = o.region;
  const charger_count = o.charger_count;
  const install_type = o.install_type;
  const summary = o.summary;
  const description = o.description;
  if (
    (typeof id !== 'number' && typeof id !== 'string') ||
    typeof title !== 'string' ||
    typeof facility_type !== 'string' ||
    typeof region !== 'string' ||
    typeof charger_count !== 'string' ||
    typeof install_type !== 'string' ||
    typeof summary !== 'string' ||
    typeof description !== 'string'
  ) {
    return null;
  }
  return {
    id,
    title,
    facility_type,
    region,
    charger_count,
    install_type,
    summary,
    description,
    main_image_url: typeof o.main_image_url === 'string' ? o.main_image_url : '',
    image_1_url: typeof o.image_1_url === 'string' ? o.image_1_url : undefined,
    image_2_url: typeof o.image_2_url === 'string' ? o.image_2_url : undefined,
    image_3_url: typeof o.image_3_url === 'string' ? o.image_3_url : undefined,
  };
}

async function loadPublishedCases(): Promise<{ rows: PublicCase[]; fromApi: boolean }> {
  try {
    const url = ev123EvcUrl('/install-cases');
    const r = await fetch(url, { method: 'GET', credentials: 'same-origin' });
    if (!r.ok) {
      return { rows: INSTALL_CASE_ITEMS.map(staticToPublic), fromApi: false };
    }
    const j: unknown = await r.json();
    if (!Array.isArray(j)) {
      return { rows: INSTALL_CASE_ITEMS.map(staticToPublic), fromApi: false };
    }
    const rows: PublicCase[] = [];
    for (const item of j) {
      const p = parsePublicCase(item);
      if (p) rows.push(p);
    }
    return { rows, fromApi: true };
  } catch {
    return { rows: INSTALL_CASE_ITEMS.map(staticToPublic), fromApi: false };
  }
}

function facilityLabel(ft: string): string {
  if (ft in FACILITY_LABEL) return FACILITY_LABEL[ft as Exclude<FacilityKey, 'all'>];
  return ft;
}

function installCaseHeroPlaceholder(): HTMLElement {
  const ph = el('div', { className: 'ev123-case-hero__ph' });
  ph.innerHTML =
    '<svg class="ev123-case-hero__ph-icon" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false"><rect x="3.5" y="5" width="17" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="9" cy="10" r="1" fill="currentColor"/><path d="M3.5 17l4-4.5 3 2.5L14 12l6.5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '<span class="ev123-case-hero__ph-text">이미지 준비 중</span>';
  return ph;
}

function openDetailModal(c: PublicCase): void {
  const stack = el('div', { className: 'ev123-install-modal-stack' });
  stack.append(
    el('p', { className: 'ev123-modal-meta', text: `건물 유형: ${facilityLabel(c.facility_type)}` }),
    el('p', { className: 'ev123-modal-meta', text: `지역: ${c.region}` }),
    el('p', { className: 'ev123-modal-meta', text: `설치 대수: ${c.charger_count}` }),
    el('p', { className: 'ev123-modal-meta', text: `설치 방식: ${c.install_type}` }),
  );
  if (c.summary) {
    stack.appendChild(el('p', { className: 'ev123-modal-meta', text: `주요 검토사항: ${c.summary}` }));
  }
  const bodyWrap = el('div', { className: 'ev123-install-detail' });
  const urls = [c.main_image_url, c.image_1_url, c.image_2_url, c.image_3_url].filter(
    (u): u is string => typeof u === 'string' && u.trim().length > 0,
  );
  if (urls.length) {
    const gal = el('div', { className: 'ev123-install-detail__gallery' });
    for (const u of urls) {
      const img = el('img', { alt: '' }) as HTMLImageElement;
      img.src = u;
      gal.appendChild(img);
    }
    bodyWrap.appendChild(gal);
  }
  bodyWrap.appendChild(el('div', { className: 'ev123-install-detail__body', text: c.description }));
  stack.appendChild(bodyWrap);
  showModal({
    title: c.title,
    body: '',
    bodyElement: stack,
    confirmLabel: '닫기',
  });
}

function renderCaseCard(c: PublicCase): HTMLElement {
  const hasImg = Boolean(c.main_image_url?.trim());
  const article = el('article', {
    className: hasImg ? 'ev123-case-card' : 'ev123-case-card ev123-case-card--no-image',
  });
  const hero = el('div', { className: 'ev123-case-hero' });
  if (hasImg) {
    const img = el('img', { alt: '' }) as HTMLImageElement;
    img.src = c.main_image_url!.trim();
    hero.appendChild(img);
  } else {
    hero.appendChild(installCaseHeroPlaceholder());
  }
  const body = el('div', { className: 'ev123-case-body' });
  const head = el('div', { className: 'ev123-case-head' });
  const badges = el('div', { className: 'ev123-case-badges' });
  badges.append(
    el('span', {
      className: installCaseFacilityBadgeClass(c.facility_type),
      text: facilityLabel(c.facility_type),
    }),
    el('span', {
      className: installCaseInstallTypeBadgeClass(c.install_type),
      text: c.install_type?.trim() || '—',
    }),
  );
  head.append(el('h3', { className: 'ev123-case-title', text: c.title }), badges);
  const meta = el('div', { className: 'ev123-case-meta' });
  const cells: [string, string][] = [
    ['지역', c.region || '—'],
    ['건물유형', facilityLabel(c.facility_type)],
    ['설치대수', c.charger_count || '—'],
    ['설치방식', c.install_type || '—'],
    ['주요 검토사항', c.summary || '—'],
  ];
  for (const [k, v] of cells) {
    const d = el('div', {});
    d.append(el('b', { text: k }), el('span', { text: v }));
    meta.appendChild(d);
  }
  const btn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--secondary ev123-btn--pill ev123-btn--sm',
    text: '내용 보기',
  }) as HTMLButtonElement;
  btn.addEventListener('click', () => openDetailModal(c));
  const actions = el('div', { className: 'ev123-case-actions' });
  actions.appendChild(btn);
  body.append(head, meta, actions);
  article.append(body, hero);
  return article;
}

export function renderInstallCaseBoardPage(root: HTMLElement): void {
  let currentFilter: FacilityKey = 'all';
  let allRows: PublicCase[] = [];
  let fromApi = false;

  const wrap = el('div', { className: 'ev123-page-install-board' });
  wrap.append(
    el('div', { className: 'ev123-page-head' }, [
      el('div', { className: 'ev123-container' }, [
        el('p', { className: 'ev123-portal-eyebrow', text: '설치사례' }),
        el('h1', {
          className: 'ev123-page-head__title',
          text: '전기차충전기 설치사례',
        }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '공동주택, 상가, 업무시설, 공공기관 등 건물 유형별 전기차충전기 설치사례를 확인하실 수 있습니다.',
        }),
      ]),
    ]),
  );

  const container = el('div', { className: 'ev123-container ev123-stack' });

  const filterRow = el('div', { className: 'ev123-board-toolbar' });
  const filters = el('div', { className: 'ev123-filters' });
  const filterButtons: HTMLButtonElement[] = [];

  const boardGrid = el('div', { className: 'ev123-case-grid' });
  const emptyState = el('div', {
    className: 'ev123-empty-state',
    style: 'display:none',
  });
  emptyState.append(
    el('p', { className: 'ev123-empty-state__text', text: '등록된 설치사례가 없습니다.' }),
    el('p', {
      className: 'ev123-empty-state__text',
      text: '설치 상담을 신청하시면 건물 조건에 맞는 설치 방향을 안내드립니다.',
    }),
  );

  function filteredRows(): PublicCase[] {
    if (currentFilter === 'all') return allRows;
    return allRows.filter((r) => r.facility_type === currentFilter);
  }

  function renderBoard(): void {
    boardGrid.innerHTML = '';
    const rows = filteredRows();
    for (const c of rows) {
      boardGrid.appendChild(renderCaseCard(c));
    }
    const showEmpty = fromApi && rows.length === 0;
    emptyState.style.display = showEmpty ? 'block' : 'none';
  }

  for (const [id, label] of FILTER_DEFS) {
    const fb = el('button', {
      type: 'button',
      className: 'ev123-filter-btn',
      text: label,
    }) as HTMLButtonElement;
    if (id === 'all') fb.classList.add('ev123-filter-btn--active');
    fb.addEventListener('click', () => {
      currentFilter = id;
      for (const b of filterButtons) {
        b.classList.toggle('ev123-filter-btn--active', b === fb);
      }
      renderBoard();
    });
    filterButtons.push(fb);
    filters.appendChild(fb);
  }

  filterRow.append(filters);

  const boardSection = el('section', { className: 'ev123-section ev123-section--relaxed ev123-section--band' });
  const boardInner = el('div', { className: 'ev123-container' });
  boardInner.append(filterRow, boardGrid, emptyState);
  boardSection.appendChild(boardInner);

  const cta = el('section', { className: 'ev123-section ev123-section--relaxed' });
  const ctaInner = el('div', { className: 'ev123-container' });
  const calcBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--primary ev123-btn--pill',
    text: '충전기 설치 계산',
  }) as HTMLButtonElement;
  calcBtn.addEventListener('click', () => navigate(ROUTES.newInstall));
  const consultBtn = el('button', {
    type: 'button',
    className: 'ev123-btn ev123-btn--secondary ev123-btn--pill',
    text: '설치 상담 신청',
  }) as HTMLButtonElement;
  consultBtn.addEventListener('click', () => navigate(ROUTES.consult));
  ctaInner.append(
    el('div', { className: 'ev123-card ev123-card--feature-wide' }, [
      el('h2', {
        className: 'ev123-card__feature-title',
        text: '우리 건물도 설치 가능 여부를 확인해보세요',
      }),
      el('div', { className: 'ev123-hero__actions ev123-hero__actions--tight' }, [calcBtn, consultBtn]),
    ]),
  );
  cta.appendChild(ctaInner);

  container.append(boardSection, cta);
  wrap.appendChild(container);
  root.appendChild(wrap);

  void (async () => {
    const { rows, fromApi: api } = await loadPublishedCases();
    allRows = rows;
    fromApi = api;
    renderBoard();
  })();
}
